import type { CustomCell, CustomRenderer } from '@glideapps/glide-data-grid'
import { getMiddleCenterBias, GridCellKind } from '@glideapps/glide-data-grid'
import type { Theme } from '@mui/material/styles'

import {
  hueToPillColorDark,
  hueToPillColorLight,
  type PillColor,
  stringToHue,
  truncateToWidth,
} from './PillCell'

export interface PillItem {
  readonly label: string
  readonly color?: string | null
}

interface MultiPillCellProps {
  readonly kind: 'multi-pill-cell'
  readonly items: readonly PillItem[]
}

export type MultiPillCell = CustomCell<MultiPillCellProps>

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        b: parseInt(result[3], 16),
        g: parseInt(result[2], 16),
        r: parseInt(result[1], 16),
      }
    : null
}

const colorToPillColor = (color: string, isDarkMode: boolean): PillColor => {
  const rgb = hexToRgb(color)
  if (!rgb) {
    const hue = stringToHue(color)
    return isDarkMode ? hueToPillColorDark(hue) : hueToPillColorLight(hue)
  }

  if (isDarkMode) {
    return {
      base: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`,
      border: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`,
      text: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`,
    }
  }

  return {
    base: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
    border: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4)`,
    text: color,
  }
}

export const getPillColor = (muiTheme: Theme, item: PillItem): PillColor => {
  if (item.color) {
    return colorToPillColor(item.color, muiTheme.palette.mode === 'dark')
  }

  const hue = stringToHue(item.label)
  return muiTheme.palette.mode === 'dark' ? hueToPillColorDark(hue) : hueToPillColorLight(hue)
}

export const multiPillCellRenderer = (muiTheme: Theme): CustomRenderer<MultiPillCell> => ({
  draw: (args, cell) => {
    const { rect, ctx, theme } = args
    const { x, y, height } = rect
    const { items } = cell.data

    if (!items || items.length === 0) {
      return true
    }

    ctx.font = `500 12px ${theme.fontFamily}`

    const padding = 8
    const gap = 6
    let currentX = x + 10
    let displayedCount = 0

    for (const item of items) {
      const color = getPillColor(muiTheme, item)
      // A single pill longer than the cell still renders, truncated — a bare
      // cell with only a "+N" pill reads as missing data.
      const maxLabelWidth = rect.width - 20 - 2 * padding
      const label =
        displayedCount === 0 ? truncateToWidth(ctx, item.label, maxLabelWidth) : item.label
      const textMetrics = ctx.measureText(label)
      const textWidth = textMetrics.width
      const textHeight = textMetrics.fontBoundingBoxAscent + textMetrics.fontBoundingBoxDescent
      const tileWidth = textWidth + 2 * padding
      const tileHeight = textHeight * 1.35

      // Check if pill would exceed cell bounds
      if (currentX + tileWidth > x + rect.width - 10) {
        break
      }

      displayedCount++

      // Draw the pill background
      if ('roundRect' in ctx) {
        ctx.fillStyle = color.base
        ctx.strokeStyle = color.border
        ctx.beginPath()
        ctx.roundRect(currentX, y + height / 2 - tileHeight / 2, tileWidth, tileHeight, 5)
        ctx.fill()
        ctx.stroke()
      }

      // Draw the text
      ctx.fillStyle = color.text
      ctx.fillText(label, currentX + padding, y + height / 2 + getMiddleCenterBias(ctx, theme))

      currentX += tileWidth + gap
    }

    // Draw remaining count if there are more items than displayed
    const remainingCount = items.length - displayedCount
    if (remainingCount > 0) {
      const remainingText = `+${remainingCount}`
      const textMetrics = ctx.measureText(remainingText)
      const textWidth = textMetrics.width
      const textHeight = textMetrics.fontBoundingBoxAscent + textMetrics.fontBoundingBoxDescent
      const tileWidth = textWidth + 2 * padding
      const tileHeight = textHeight * 1.35

      if (currentX + tileWidth <= x + rect.width - 10) {
        // Use neutral color for the count pill
        const neutralHue = 0
        const color =
          muiTheme.palette.mode === 'dark'
            ? hueToPillColorDark(neutralHue)
            : hueToPillColorLight(neutralHue)

        if ('roundRect' in ctx) {
          ctx.fillStyle = color.base
          ctx.strokeStyle = color.border
          ctx.beginPath()
          ctx.roundRect(currentX, y + height / 2 - tileHeight / 2, tileWidth, tileHeight, 5)
          ctx.fill()
          ctx.stroke()
        }

        ctx.fillStyle = color.text
        ctx.fillText(
          remainingText,
          currentX + padding,
          y + height / 2 + getMiddleCenterBias(ctx, theme),
        )
      }
    }

    return true
  },
  isMatch: (c): c is MultiPillCell => (c.data as MultiPillCellProps).kind === 'multi-pill-cell',
  kind: GridCellKind.Custom,
})
